import {
  addDays,
  addHours,
  endOfDay,
  format,
  isBefore,
  startOfDay,
  startOfHour,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { groupBy } from 'lodash-es';
import { stringToUtcDate } from 'src/utils/dateTimeUtils';
import { AggregateMetricsTimePeriod, Sale } from 'src/WebApiController';

import { ChartType } from '../ChartTypeSelector';
import { SaleChartData } from '../DataInsightsContext';

const getGranularityV1 = (
  period: AggregateMetricsTimePeriod
): 'hour' | '3hour' | 'day' | 'week' | 'month' => {
  switch (period) {
    case AggregateMetricsTimePeriod.Today:
    case AggregateMetricsTimePeriod.LastWeek:
      return 'hour';
    case AggregateMetricsTimePeriod.LastMonth:
    case AggregateMetricsTimePeriod.LastThreeMonths:
      return 'day';
    case AggregateMetricsTimePeriod.Ytd:
      return 'week';
    case AggregateMetricsTimePeriod.LastYear:
    case AggregateMetricsTimePeriod.LastFiveYears:
    case AggregateMetricsTimePeriod.All:
      return 'month';
    default:
      throw new Error(`Unsupported period: ${period}`);
  }
};

const getGranularity = (
  period: AggregateMetricsTimePeriod
): 'hour' | '6hour' | 'day' | '3day' | 'week' | 'month' => {
  switch (period) {
    case AggregateMetricsTimePeriod.Today:
      return 'hour';
    case AggregateMetricsTimePeriod.LastWeek:
      return '6hour';
    case AggregateMetricsTimePeriod.LastMonth:
      return 'day';
    case AggregateMetricsTimePeriod.LastThreeMonths:
      return '3day';
    case AggregateMetricsTimePeriod.Ytd:
      return 'week';
    case AggregateMetricsTimePeriod.LastYear:
    case AggregateMetricsTimePeriod.LastFiveYears:
    case AggregateMetricsTimePeriod.All:
      return 'month';
    default:
      throw new Error(`Unsupported period: ${period}`);
  }
};

export const formatByGranularity = (
  saleDate: string,
  period: AggregateMetricsTimePeriod,
  timeZone: string,
  useDecreasedGranularity: boolean
) => {
  const granularity = useDecreasedGranularity
    ? getGranularity(period)
    : getGranularityV1(period);
  const utcDate = stringToUtcDate(saleDate);
  const localDate = toZonedTime(utcDate, timeZone);

  switch (granularity) {
    case 'hour':
      return format(utcDate, 'yyyy-MM-dd HH');
    case '6hour': {
      const hour = utcDate.getHours();
      const bucket = Math.floor(hour / 6) * 6;
      const bucketDate = new Date(utcDate);
      bucketDate.setHours(bucket, 0, 0, 0);
      return format(bucketDate, 'yyyy-MM-dd HH');
    }
    case 'day':
      return format(startOfDay(localDate), 'yyyy-MM-dd');
    case '3day': {
      const start = startOfDay(localDate);
      const dayOffset = start.getDate() % 3;
      const bucketDate = addDays(start, -dayOffset);
      return format(
        startOfDay(toZonedTime(bucketDate, timeZone)),
        'yyyy-MM-dd'
      );
    }
    case 'week':
      return format(startOfWeek(localDate), 'yyyy-MM-dd');
    case 'month':
      return format(startOfMonth(localDate), 'yyyy-MM');
  }
};

// --- Helper Function to Generate Time Slots ---
export const generateTimeSlots = (
  lastSaleDateString: string,
  timeZone: string
): SaleChartData[] => {
  const startDate = startOfHour(addHours(new Date(lastSaleDateString), 1));
  const endDate = startOfHour(
    startOfDay(addDays(new Date(lastSaleDateString), 1))
  );

  let currHour = startDate;
  const timeSlots: SaleChartData[] = [];
  while (isBefore(currHour, endDate)) {
    const formattedDate = formatInTimeZone(currHour, timeZone, 'yyyy-MM-dd HH');
    const dateString = currHour.toISOString();
    const timeSlot: SaleChartData = {
      dateTime: formattedDate,
      totalPnl: null,
      totalRevenue: null,
      totalQuantity: null,
      marketshareQuantity: null,
      marketshare: null,
    };
    timeSlots.push(timeSlot);
    currHour = addHours(currHour, 1);
  }

  return timeSlots;
};
